<div
	class="stamps-container"
	[formGroup]="parentForm()"
>
	<!-- Stamp List Section -->
	<div class="stamps-section">
		<h3 class="section-title">{{ 'user.form.section.stamps.available' | translate }}</h3>

		@if (!loading() && stamps()) {
			<div class="stamps-list">
				<!-- Empty state when no stamps -->
				@if (stamps().length === 0) {
					<div class="empty-stamps-state">
						<div class="empty-stamp-icon">
							<icon icon="fad fa-stamp fa-2x" />
						</div>
						<p class="empty-stamp-text">{{ 'user.form.section.stamps.no-stamps' | translate }}</p>
					</div>
				}
				<!-- Stamps grid -->
				<div class="stamps-grid">
					@for (stamp of stamps(); track stamp) {
						@let isDefaultStamp = isDefaultStampFactory(stamp);
						@let isFixed = stamp.isFixed;
						<div
							class="stamp-item"
							[class.stamp-selected]="isDefaultStamp()"
							[class.stamp-fixed]="isFixed"
							tabindex="0"
							role="button"
							[attr.aria-label]="'user.form.section.stamps.select-stamp' | translate: {name: stamp.name}"
							[attr.aria-pressed]="isDefaultStamp()"
						>
							<div class="stamp-image-container">
								@if (isFixed) {
									<icon [icon]="stamp.icon" />
								} @else {
									<img
										[src]="stamp.storedFileImage.webEncode"
										[alt]="stamp.name"
										class="stamp-image"
									/>
								}
								<div class="stamp-overlay">
									<!-- Set As Default Stamp Button -->
									<button
										type="button"
										class="stamp-default-btn"
										(click)="setAsDefaultStamp(stamp); $event.stopPropagation()"
										[attr.aria-label]="'user.form.section.stamps.set-as-default' | translate: {name: stamp.name}"
									>
										<icon icon="fad fa-star fa-fw" />
									</button>
									<!-- Edit Stamp Button - only show for non-fixed stamps -->
									@if (!isFixed) {
										<!-- Delete Stamp Button - only show for non-fixed stamps -->
										<button
											type="button"
											class="stamp-delete-btn"
											(click)="deleteStamp(stamp); $event.stopPropagation()"
											[attr.aria-label]="'user.form.section.stamps.delete-stamp' | translate: {name: stamp.name}"
										>
											<icon icon="fad fa-trash fa-fw" />
										</button>
									}
								</div>
								@if (isDefaultStamp()) {
									<div class="default-stamp-badge">
										{{ 'user.form.section.stamps.default' | translate }}
									</div>
								}
								<!-- Fixed stamp indicator -->
								@if (isFixed) {
									<div class="fixed-stamp-badge">
										{{ stamp.name }}
									</div>
								}
							</div>
							<div class="stamp-name">{{ stamp.name }}</div>
						</div>
					}
					<!-- Add new stamp tile -->
					<div
						class="stamp-item stamp-add-new"
						(click)="openAddStampModal()"
						(keydown.enter)="openAddStampModal()"
						(keydown.space)="openAddStampModal()"
						tabindex="0"
						role="button"
						[attr.aria-label]="'user.form.section.stamps.add-new-stamp' | translate"
					>
						<div class="stamp-add-icon">
							<icon icon="fad fa-plus" />
						</div>
						<div class="stamp-add-text">{{ 'user.form.section.stamps.add-new' | translate }}</div>
					</div>
				</div>
			</div>
		}

		<!-- Loading state -->
		@if (loading()) {
			<div class="stamps-loading">
				<div class="spinner"></div>
				<p>{{ 'user.form.section.stamps.loading' | translate }}</p>
			</div>
		}
	</div>

	<!-- Add/Edit Stamp Modal -->
	@if (addStampModalOpen() || editStampData()) {
		<div
			class="stamp-modal-backdrop"
			(click)="closeStampModal()"
			(keydown.escape)="closeStampModal()"
			tabindex="-1"
			role="dialog"
			aria-modal="true"
			aria-labelledby="stamp-modal-title"
		>
			<div
				class="stamp-modal"
				(click)="$event.stopPropagation()"
				(keydown)="$event.stopPropagation()"
				tabindex="0"
				role="dialog"
				aria-labelledby="stamp-modal-title"
			>
				<div class="stamp-modal-header">
					<h3 id="stamp-modal-title">
						{{ editStampData() ? ('user.form.section.stamps.edit-stamp' | translate: {name: stampFormData().name}) : ('user.form.section.stamps.add-stamp' | translate) }}
					</h3>
					<button
						type="button"
						class="stamp-modal-close"
						(click)="closeStampModal()"
						aria-label="Close modal"
					>
						<icon icon="fad fa-times" />
					</button>
				</div>
				<div class="stamp-modal-content">
					<!-- Stamp Name -->
					<div class="stamp-form-field">
						<label for="stampName">{{ 'user.form.section.stamps.name' | translate }}</label>
						<input
							type="text"
							id="stampName"
							[ngModel]="stampFormData().name"
							(ngModelChange)="patchStampFormData({name: $event})"
							[ngModelOptions]="{standalone: true}"
							class="stamp-input"
							required
						/>
						@for (error of stampNameErrors$(); track error) {
							@if (error) {
								<div class="stamp-error">{{ error }}</div>
							}
						}
					</div>
					<!-- Stamp Creation Type Selector -->
					<div
						class="stamp-creation-selector"
						role="radiogroup"
						aria-label="Stamp creation method"
					>
						<button
							type="button"
							class="stamp-create-option"
							[class.active]="stampCreationMode() === 'upload'"
							(click)="setStampCreationMode('upload')"
							role="radio"
							[attr.aria-checked]="stampCreationMode() === 'upload'"
						>
							<icon icon="fad fa-upload" />
							{{ 'user.form.section.stamps.upload-image' | translate }}
						</button>
						<button
							type="button"
							class="stamp-create-option"
							[class.active]="stampCreationMode() === 'draw'"
							(click)="setStampCreationMode('draw')"
							role="radio"
							[attr.aria-checked]="stampCreationMode() === 'draw'"
						>
							<icon icon="fad fa-pencil" />
							{{ 'user.form.section.stamps.draw-image' | translate }}
						</button>
					</div>
					<!-- Upload Image Section -->
					@if (stampCreationMode() === 'upload') {
						<div class="stamp-upload-container">
							<div
								class="stamp-upload-area"
								[class.has-preview]="stampImagePreview()"
								(dragover)="onDragOver($event)"
								(dragleave)="onDragLeave($event)"
								(drop)="onDrop($event)"
								(click)="triggerFileInput()"
								(keydown.enter)="triggerFileInput()"
								(keydown.space)="triggerFileInput()"
								role="button"
								tabindex="0"
								[attr.aria-label]="'user.form.section.stamps.drop-or-click' | translate"
							>
								<input
									type="file"
									#fileInput
									style="display: none"
									accept="image/*"
									(change)="onFileSelected($event)"
									aria-label="Upload stamp image"
								/>
								@if (!stampImagePreview()) {
									<div class="stamp-upload-placeholder">
										<icon icon="fad fa-upload fa-fw" />
										<p>{{ 'user.form.section.stamps.drop-or-click' | translate }}</p>
									</div>
								}
								@if (stampImagePreview()) {
									<div class="stamp-preview">
										<img
											[src]="stampImagePreview()"
											alt="Stamp Preview"
											class="stamp-preview-img h-full object-fill"
										/>
									</div>
								}
							</div>
						</div>
					}
					<!-- Drawing Canvas Section -->
					@if (stampCreationMode() === 'draw') {
						<div class="stamp-drawing-container">
							<div
								class="stamp-drawing-tools"
								role="toolbar"
								aria-label="Drawing tools"
							>
								<!-- Tool 1: Color Picker -->
								<input
									type="color"
									[(ngModel)]="penColor"
									(click)="setDrawingTool('pen')"
									[ngModelOptions]="{standalone: true}"
									class="stamp-drawing-color"
									[matTooltip]="'user.form.section.stamps.color-picker' | translate"
								/>
								<!-- Tool 2: Brush Size -->
								<input
									type="range"
									[(ngModel)]="brushSize"
									[ngModelOptions]="{standalone: true}"
									min="1"
									max="50"
									step="1"
									class="stamp-drawing-brush-size"
								/>
								<!-- Tool 3: Toggle Drawing Tool -->
								<button
									type="button"
									class="stamp-drawing-tool"
									(click)="setDrawingTool(null)"
									[matTooltip]="'user.form.section.stamps.toggle-tool' | translate: {tool: 'user.form.section.stamps.toggle-tool-' + (drawingTool() === 'pen' ? 'eraser' : 'pen') | translate}"
								>
									<icon [icon]="drawingTool() === 'pen' ? 'fad fa-eraser' : 'fad fa-pencil'" />
								</button>
								<!-- Tool 4: Clear Canvas -->
								<button
									type="button"
									class="stamp-drawing-tool"
									(click)="clearDrawing()"
									[matTooltip]="'user.form.section.stamps.clear-canvas' | translate"
								>
									<icon icon="fad fa-trash" />
								</button>
							</div>
							<div class="stamp-canvas-container">
								<canvas
									#stampCanvas
									class="stamp-canvas"
									role="img"
									aria-label="Drawing canvas"
									[class.pen-tool]="drawingTool() === 'pen'"
									[class.eraser-tool]="drawingTool() === 'eraser'"
								></canvas>
							</div>
						</div>
					}
				</div>
				<div class="stamp-modal-footer">
					<button
						type="button"
						class="stamp-modal-cancel"
						(click)="closeStampModal()"
					>
						{{ 'user.form.section.stamps.cancel' | translate }}
					</button>
					<button
						type="button"
						class="stamp-modal-save"
						[disabled]="!canSaveStamp()"
						(click)="saveStamp()"
					>
						{{ 'user.form.section.stamps.save' | translate }}
					</button>
				</div>
			</div>
		</div>
	}
</div>
